/**
 * The driver catalogue: which drivers a tenant prices with, defined by a platform operator when
 * the tenant is created. Weights are NOT part of it — they arrive with a model version. The
 * catalogue carries what the calibration app cannot: the meaning of each level, so the tool can
 * explain a rung to a rep and an admin can edit a weight knowing what "4" stands for.
 */
import { z } from 'zod'
import { BLOCK_KEYS } from './schema.js'
import type { Block, BlockKey, DriverKind, Grain, ModelPayload, Policy, PricingRules } from './types.js'

export interface CatalogueDriver {
  id: string
  name: string
  nameDe?: string
  block: BlockKey
  kind: DriverKind
  grain: Grain
  /** ordered level keys, e.g. ["1","2","3","4","5"] or ["3","6","12"] */
  levels: string[]
  /** display labels aligned with `levels`, e.g. ["3 months","6 months","12 months"]; keys are shown when absent */
  levelLabels?: string[]
  /** the same in German, for a German UI */
  levelLabelsDe?: string[]
  /** what the lowest and highest level mean */
  anchorLow?: string
  anchorHigh?: string
  /** the scale reads against its natural magnitude (e.g. "share of the customer's cost": 1 = large) */
  reversed?: boolean
  /** fitted by the calibration app, or set centrally / by policy */
  fitted?: boolean
  /** free text: where the ratings come from, who sets them */
  source?: string
}

export interface Catalogue {
  blocks: Block[]
  drivers: CatalogueDriver[]
}

const ID_RE = /^[a-z][a-z0-9_]{1,40}$/

export const catalogueSchema = z
  .object({
    blocks: z
      .array(z.object({ key: z.enum(BLOCK_KEYS), name: z.string().min(1), nameDe: z.string().optional() }))
      .superRefine((blocks, ctx) => {
        const keys = blocks.map((b) => b.key)
        if (keys.length !== BLOCK_KEYS.length || BLOCK_KEYS.some((k) => !keys.includes(k))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `blocks must contain exactly ${BLOCK_KEYS.join(', ')}` })
        }
      }),
    drivers: z
      .array(
        z.object({
          id: z.string().regex(ID_RE, 'driver id: lowercase letters, digits and underscores, 2–41 chars'),
          name: z.string().min(1),
          nameDe: z.string().optional(),
          block: z.enum(BLOCK_KEYS),
          kind: z.enum(['relative', 'absolute']),
          grain: z.enum(['product', 'customer', 'market', 'deal']),
          levels: z.array(z.string().min(1)).min(2, 'a driver needs at least two levels'),
          levelLabels: z.array(z.string()).optional(),
          levelLabelsDe: z.array(z.string()).optional(),
          anchorLow: z.string().optional(),
          anchorHigh: z.string().optional(),
          reversed: z.boolean().optional(),
          fitted: z.boolean().optional(),
          source: z.string().optional(),
        }),
      )
      .min(1)
      .superRefine((drivers, ctx) => {
        const seen = new Set<string>()
        for (const d of drivers) {
          if (seen.has(d.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate driver id "${d.id}"` })
          seen.add(d.id)
          if (new Set(d.levels).size !== d.levels.length) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `driver "${d.id}" has duplicate levels` })
          }
          if (d.levelLabels && d.levelLabels.length !== d.levels.length) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `driver "${d.id}": levelLabels must have one entry per level` })
          }
          if (d.levelLabelsDe && d.levelLabelsDe.length !== d.levels.length) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `driver "${d.id}": levelLabelsDe must have one entry per level` })
          }
        }
      }),
  })
  .strict()

export function parseCatalogue(input: unknown): Catalogue {
  return catalogueSchema.parse(input) as Catalogue
}

export function safeParseCatalogue(input: unknown) {
  const r = catalogueSchema.safeParse(input)
  return r.success ? { success: true as const, data: r.data as Catalogue } : { success: false as const, error: r.error }
}

export const DEFAULT_POLICY: Policy = {
  concessionRate: 0.05,
  capRate: 0.08,
  floorIncludesBlocks: ['market'],
  authority: { repFloor: 'target', hardFloor: 'floor' },
  costMoveBasis: 'cost',
}

/** The rules a tenant starts with: no uplift, the default price rules and approval limits. */
export const DEFAULT_RULES: PricingRules = { uplift: {}, policy: DEFAULT_POLICY }

/**
 * A model version built from a catalogue with every coefficient at zero. It prices at cost
 * plus uplift until an admin enters weights or a calibrated version is imported. The admin's
 * blast-radius preview then shows the movement from this baseline.
 */
export function scaffoldModel(
  catalogue: Catalogue,
  opts: { publishedBy: string; note?: string; basis?: 'ex_works' | 'delivered'; currency?: string },
): ModelPayload {
  const drivers = catalogue.drivers.map((d) => {
    const driver: ModelPayload['drivers'][number] = {
      id: d.id,
      name: d.name,
      block: d.block,
      kind: d.kind,
      grain: d.grain,
      effects: d.levels.map((level) => ({ level, value: 0 })),
    }
    if (d.nameDe !== undefined) driver.nameDe = d.nameDe
    return driver
  })
  return {
    schemaVersion: 1,
    publishedAt: new Date().toISOString(),
    publishedBy: opts.publishedBy,
    note: opts.note ?? 'Scaffolded from the driver catalogue; all weights zero',
    provenance: { source: 'hand_edited', parentVersionId: 'catalogue', editedEffects: [], editedOther: [] },
    basis: opts.basis ?? 'ex_works',
    currency: opts.currency ?? 'EUR',
    intercept: 0,
    absBase: 0,
    blocks: catalogue.blocks.map((b) => (b.nameDe !== undefined ? { key: b.key, name: b.name, nameDe: b.nameDe } : { key: b.key, name: b.name })),
    drivers,
  }
}

/** Driver ids a model version declares that the catalogue does not know. */
export function unknownDrivers(catalogue: Catalogue, model: Pick<ModelPayload, 'drivers'>): string[] {
  const known = new Set(catalogue.drivers.map((d) => d.id))
  return model.drivers.map((d) => d.id).filter((id) => !known.has(id))
}
